/**
 * "Search" style tile. Not that it isn't just used on the search results page,
 * but also search-ish places such as destination product pages.
 */
import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import BedbankSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankSearchTile'
import HotelBundleSearchTile from 'components/SearchKit/SearchTiles/accommodations/HotelBundleSearchTile'
import HotelSearchTile from 'components/SearchKit/SearchTiles/accommodations/HotelSearchTile'
import VillaSearchTile from 'components/SearchKit/SearchTiles/accommodations/VillaSearchTile'
import CruiseOfferTile from 'components/SearchKit/SearchTiles/cruises/CruiseOfferTile'
import TourV2SearchTile from 'components/SearchKit/SearchTiles/tours/TourV2SearchTile'
import config from 'constants/config'
import {
  OFFER_TYPE_BED_BANK,
} from 'constants/offer'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import noop from 'lib/function/noop'
import { isBedbankOffer, isBundleOffer, isCruiseOffer, isCruiseV1Offer, isLEHotel, isTourV2Offer, isVillaOffer } from 'lib/offer/offerTypes'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import React, { forwardRef, useCallback } from 'react'
import styled from 'styled-components'
import { TopLevelTileProps } from '../OfferTileTypes'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import useOfferList from 'hooks/Offers/useOfferList'
import BedbankMetaSearchTile from 'components/SearchKit/SearchTiles/accommodations/BedbankMetaSearchTile'
import { OfferUrlProvider } from 'contexts/OfferUrlContext'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { useAppSelector } from 'hooks/reduxHooks'
import { shouldShowOfferDescription } from 'lib/offer/offerDescriptionUtils'

const OfferLink = styled(Clickable)`
  position: relative;
`

function getTestId(offer: App.AnyOffer) {
  let flightsEnabled
  let onlinePurchaseSchedule

  if (offer.type !== OFFER_TYPE_BED_BANK) {
    const internalOffer = offer as App.Offer

    flightsEnabled = internalOffer.offerFlightsEnabled
    onlinePurchaseSchedule = internalOffer.onlinePurchaseSchedule
  }

  return clsx(
    `offer-${offer.type}`, {
      'offer-with-flights': flightsEnabled,
      [`Purchasable-${offer.type}`]: scheduleIsCurrent(onlinePurchaseSchedule),
    })
}

interface Props extends TopLevelTileProps {
  className?: string
  position?: number
  showLimitedTimeLuxExclusiveTooltip?: boolean
  preventNavigationToOfferPage?: boolean
  storeOfferSearchTile?: boolean
}

const OfferSearchTile = forwardRef<HTMLButtonElement, Props>((props, ref) => {
  const {
    offer,
    filters,
    productClick = noop,
    className,
    isMetaSearch = false,
    showPerNightPricing = false,
    showLimitedTimeLuxExclusiveTooltip = false,
    preventNavigationToOfferPage = false,
    storeOfferSearchTile = false,
    position,
  } = props

  const offerMetaData = useOfferMetaData(offer.id, filters)
  const hidePrice = useHideAnytimePrices(offer.type, !!offerMetaData?.hidePricing)

  const searchOfferList = useOfferList(filters)
  const descriptionLoading = searchOfferList?.descriptionLoading

  const utmBp = useAppSelector(state => state.utm.bp)

  const offerUrl = useOfferUrl(offer, {
    filters,
    bundledOfferId: offerMetaData?.bundledOfferId,
    offerLinkIncludesFilters: true,
    searchId: searchOfferList.searchId,
    showDatePicker: hidePrice,
    utmBp: isMetaSearch ? utmBp : undefined,
  })
  useOfferBestPrice(offer, filters, { disabled: getShouldUseSearchPrices(filters) })

  const handleClick = useCallback((e?: React.MouseEvent) => {
    if (preventNavigationToOfferPage && e) {
      e.preventDefault()
      e.stopPropagation()
    }
    productClick?.(offer)
  }, [offer, productClick, preventNavigationToOfferPage])

  return <ProductPaletteProvider product={offer}>
    <OfferUrlProvider url={offerUrl}>
      <OfferLink
        onClick={handleClick}
        to={preventNavigationToOfferPage ? undefined : offerUrl}
        as={preventNavigationToOfferPage ? 'div' : undefined}
        data-testid={getTestId(offer)}
        target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
        className={className}
        ref={ref}
      >
        {isLEHotel(offer) && <HotelSearchTile
          offer={offer}
          filters={filters}
          showPerNightPricing={showPerNightPricing}
          showLimitedTimeLuxExclusiveTooltip={showLimitedTimeLuxExclusiveTooltip}
          storeSearchTile={storeOfferSearchTile}
          showDescription={shouldShowOfferDescription(position, descriptionLoading)}
          position={position}
        />}
        {isTourV2Offer(offer) && <TourV2SearchTile
          offer={offer}
          filters={filters}
        />}
        {isBedbankOffer(offer) && !isMetaSearch && <BedbankSearchTile
          offer={offer}
          filters={filters}
          showDescription={shouldShowOfferDescription(position, descriptionLoading)}
          position={position}
        />}
        {isBedbankOffer(offer) && isMetaSearch && <BedbankMetaSearchTile
          offer={offer}
          filters={filters}
        />}
        {isVillaOffer(offer) && <VillaSearchTile
          offer={offer}
          filters={filters}
          showDescription={shouldShowOfferDescription(position, descriptionLoading)}
          position={position}
        />}
        {(isCruiseV1Offer(offer) || isCruiseOffer(offer)) && <CruiseOfferTile
          offer={offer}
          filters={filters}
        />}
        {isBundleOffer(offer) && <HotelBundleSearchTile
          offer={offer}
          filters={filters}
          showDescription={shouldShowOfferDescription(position, descriptionLoading)}
          position={position}
        />}
      </OfferLink>
    </OfferUrlProvider>
  </ProductPaletteProvider>
})

export default OfferSearchTile
